import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getHubbleWallet,
  getStoreOrders,
  getStoreProducts,
  getStoreSettings,
  getStoreApiPayload,
  isStoreApiSuccess,
  syncHubbleBrands,
  syncHubbleDiscounts,
} from '../../services/storeApi';
import {
  formatAddressShort,
  formatDateTime,
  formatInr,
  formatProductStockLabel,
  getOrderAddress,
  getProductInventory,
  FULFILLMENT_STATUS_LABELS,
  isLimitedStock,
  ORDER_STATUS_LABELS,
} from '../Store/storeLabels';
import { useStoreSession } from '../Store/useStoreSession';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const LOW_WALLET_THRESHOLD = 10000;
const LOW_STOCK_THRESHOLD = 5;

function StoreDashboard() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState('');
  const [wallet, setWallet] = useState(null);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { showSnackbar, handleSessionExpired } = useStoreSession(setSnackbar);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, walletRes, ordersRes, productsRes] = await Promise.all([
        getStoreSettings(),
        getHubbleWallet(),
        getStoreOrders({ limit: 200, offset: 0 }),
        getStoreProducts({ limit: 200, offset: 0 }),
      ]);

      if (
        handleSessionExpired(settingsRes) ||
        handleSessionExpired(walletRes) ||
        handleSessionExpired(ordersRes) ||
        handleSessionExpired(productsRes)
      ) {
        return;
      }

      if (isStoreApiSuccess(settingsRes)) {
        setSettings(getStoreApiPayload(settingsRes));
      } else {
        setSettings(null);
        showSnackbar(settingsRes?.message || 'Failed to load store settings', 'error');
      }

      if (isStoreApiSuccess(walletRes)) {
        setWallet(getStoreApiPayload(walletRes));
      } else {
        setWallet(null);
        showSnackbar(walletRes?.message || 'Failed to load Hubble wallet', 'error');
      }

      if (isStoreApiSuccess(ordersRes)) {
        const payload = getStoreApiPayload(ordersRes);
        setOrders(payload?.orders || []);
      } else {
        setOrders([]);
      }

      if (isStoreApiSuccess(productsRes)) {
        const payload = getStoreApiPayload(productsRes);
        setProducts(payload?.products || []);
      } else {
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const stats = useMemo(() => {
    const openMerch = orders.filter(
      (o) => o.product_type === 'DUGOUT_PRODUCT' && o.fulfillment_status === 'MANUAL_REQUIRED'
    ).length;
    const processingGiftCards = orders.filter((o) => o.status === 'PROCESSING').length;
    const failedOrRefunded = orders.filter((o) => ['FAILED', 'REFUNDED'].includes(o.status)).length;
    return { openMerch, processingGiftCards, failedOrRefunded };
  }, [orders]);

  const merchQueuePreview = useMemo(
    () =>
      orders
        .filter((o) => o.product_type === 'DUGOUT_PRODUCT' && o.fulfillment_status === 'MANUAL_REQUIRED')
        .slice(0, 8),
    [orders]
  );

  const limitedStockMerch = useMemo(
    () =>
      products.filter(
        (p) => p.product_type === 'DUGOUT_PRODUCT' && p.is_active !== false && isLimitedStock(p)
      ),
    [products]
  );

  const lowStockMerch = useMemo(
    () =>
      limitedStockMerch.filter((p) => {
        const { stockQuantity } = getProductInventory(p);
        return stockQuantity != null && stockQuantity <= LOW_STOCK_THRESHOLD;
      }),
    [limitedStockMerch]
  );

  const limitedStockPreview = useMemo(() => lowStockMerch.slice(0, 8), [lowStockMerch]);

  const runSync = async (kind) => {
    setSyncing(kind);
    try {
      const res = kind === 'brands' ? await syncHubbleBrands() : await syncHubbleDiscounts();
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Sync failed', 'error');
        return;
      }
      const synced = getStoreApiPayload(res)?.synced;
      const message =
        kind === 'brands'
          ? synced != null
            ? `Synced ${synced} brand(s). Enable listed products in Products.`
            : 'Hubble brands synced.'
          : synced != null
            ? `Synced ${synced} discount(s). Set overrides in Discounts.`
            : 'Hubble discounts synced.';
      showSnackbar(message, 'success');
      loadOverview();
    } finally {
      setSyncing('');
    }
  };

  const fePerInr = settings?.fan_energy_per_inr ?? '—';
  const balance = wallet?.balance;
  const lowWallet = balance != null && Number(balance) < LOW_WALLET_THRESHOLD;

  const setupSteps = [
    'Confirm Fan Energy per ₹ in Settings',
    'Sync Hubble brands',
    'Sync Hubble discounts',
    'Enable Listed on gift cards',
    'Set discount overrides',
    'Add merch categories and products',
    'Check Hubble wallet · monitor orders',
  ];

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Store Dashboard</h3>
                  <Typography variant="body2" color="text.secondary">
                    Hubble gift cards, merch categories, shipping from address book, Fan Energy pricing
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Back
                      </Button>
                      <Button variant="contained" onClick={() => runSync('brands')} disabled={!!syncing || loading}>
                        {syncing === 'brands' ? 'Syncing Hubble brands…' : 'Sync Hubble brands'}
                      </Button>
                      <Button variant="contained" onClick={() => runSync('discounts')} disabled={!!syncing || loading}>
                        {syncing === 'discounts' ? 'Syncing Hubble discounts…' : 'Sync Hubble discounts'}
                      </Button>
                      <Button component={Link} to="/admin/store/settings" variant="outlined" startIcon={<SettingsIcon />}>
                        Settings
                      </Button>
                      <Button component={Link} to="/admin/store/categories" variant="outlined">
                        Categories
                      </Button>
                      <Button component={Link} to="/admin/store/discounts" variant="outlined">
                        Discounts
                      </Button>
                      <Button component={Link} to="/admin/store/products" variant="outlined">
                        Products
                      </Button>
                      <Button component={Link} to="/admin/store/orders" variant="outlined">
                        Orders
                      </Button>
                      <Button variant="text" onClick={loadOverview} disabled={loading}>
                        Refresh
                      </Button>
                    </Box>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        {lowWallet && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            Hubble wallet balance is low ({formatInr(balance)}). Top up before go-live; server emails ops
                            when balance is critical.
                          </Alert>
                        )}

                        {lowStockMerch.length > 0 && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {lowStockMerch.length} listed merch product(s) have limited stock at or below{' '}
                            {LOW_STOCK_THRESHOLD} units. Review in Products before they sell out.
                          </Alert>
                        )}

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Hubble wallet balance
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                  {balance != null ? formatInr(balance) : '—'}
                                </Typography>
                                {wallet?.snapshot?.created_at && (
                                  <Typography variant="caption" color="text.secondary">
                                    Snapshot: {new Date(wallet.snapshot.created_at).toLocaleString()}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" component={Link} to="/admin/store/settings" sx={{ textDecoration: 'none', color: 'inherit' }}>
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Fan Energy per ₹1
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                  {fePerInr}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ₹100 ≈ {fePerInr !== '—' ? Math.ceil(100 * Number(fePerInr)) : '—'} FE
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card
                              variant="outlined"
                              component={Link}
                              to="/admin/store/orders?tab=merch_queue"
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                borderColor: stats.openMerch ? 'warning.main' : undefined,
                              }}
                            >
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Open merch orders
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                  {stats.openMerch}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {FULFILLMENT_STATUS_LABELS.MANUAL_REQUIRED} — includes shipping address
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card
                              variant="outlined"
                              component={Link}
                              to="/admin/store/orders?tab=processing"
                              sx={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Processing gift cards
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                  {stats.processingGiftCards}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  status = PROCESSING · failed/refunded: {stats.failedOrRefunded}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card
                              variant="outlined"
                              component={Link}
                              to="/admin/store/products"
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                borderColor: lowStockMerch.length ? 'warning.main' : undefined,
                              }}
                            >
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Limited stock merch
                                </Typography>
                                <Typography variant="h4" sx={{ mt: 1 }}>
                                  {limitedStockMerch.length}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {lowStockMerch.length} low (≤{LOW_STOCK_THRESHOLD}) · quantity shown only for LIMITED
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Recommended setup flow
                          </Typography>
                          <Stepper activeStep={-1} alternativeLabel sx={{ mt: 1 }}>
                            {setupSteps.map((label) => (
                              <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                              </Step>
                            ))}
                          </Stepper>
                        </Paper>

                        {limitedStockPreview.length > 0 && (
                          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1">Low limited-stock merch</Typography>
                              <Button component={Link} to="/admin/store/products" size="small">
                                View products
                              </Button>
                            </Box>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Listed</TableCell>
                                    <TableCell>Stock</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {limitedStockPreview.map((p) => (
                                    <TableRow key={p.id} hover>
                                      <TableCell>{p.title}</TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          label={p.is_listed ? 'Listed' : 'Hidden'}
                                          color={p.is_listed ? 'success' : 'default'}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip size="small" color="warning" label={formatProductStockLabel(p)} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        )}

                        {merchQueuePreview.length > 0 && (
                          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1">Merch queue (action needed)</Typography>
                              <Button component={Link} to="/admin/store/orders?tab=merch_queue" size="small">
                                View all
                              </Button>
                            </Box>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>FE</TableCell>
                                    <TableCell>Ship to</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {merchQueuePreview.map((o) => (
                                    <TableRow key={o.id} hover>
                                      <TableCell>{o.user_id}</TableCell>
                                      <TableCell>#{o.store_product_id}</TableCell>
                                      <TableCell>{o.fan_energy_amount}</TableCell>
                                      <TableCell>{formatAddressShort(getOrderAddress(o))}</TableCell>
                                      <TableCell>
                                        <Chip size="small" label={ORDER_STATUS_LABELS[o.status] || o.status} />
                                      </TableCell>
                                      <TableCell>{formatDateTime(o.created_at)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        )}

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Link to="/admin/store/categories" style={{ textDecoration: 'none' }}>
                              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', bgcolor: '#2E4053', color: '#fff', minHeight: 120 }}>
                                <Typography variant="h6">Categories</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                                  Hubble + custom merch categories
                                </Typography>
                              </Paper>
                            </Link>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Link to="/admin/store/discounts" style={{ textDecoration: 'none' }}>
                              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', bgcolor: '#B9770E', color: '#fff', minHeight: 120 }}>
                                <Typography variant="h6">Discounts</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                                  brand overrides · Fan Energy vs cash split
                                </Typography>
                              </Paper>
                            </Link>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Link to="/admin/store/products" style={{ textDecoration: 'none' }}>
                              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', bgcolor: '#1A5276', color: '#fff', minHeight: 120 }}>
                                <Typography variant="h6">Products</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                                  category_key, listed/active, bulk gift cards
                                </Typography>
                              </Paper>
                            </Link>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Link to="/admin/store/orders" style={{ textDecoration: 'none' }}>
                              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', bgcolor: '#117A65', color: '#fff', minHeight: 120 }}>
                                <Typography variant="h6">Orders</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                                  shipping_address · address book ID
                                </Typography>
                              </Paper>
                            </Link>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Link to="/admin/store/settings" style={{ textDecoration: 'none' }}>
                              <Paper elevation={3} sx={{ p: 3, textAlign: 'center', bgcolor: '#6C3483', color: '#fff', minHeight: 120 }}>
                                <Typography variant="h6">Settings</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                                  fan_energy_per_inr — re-sync after change
                                </Typography>
                              </Paper>
                            </Link>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default StoreDashboard;
